<template>
  <div class="lens-inspiration-container">
    <!-- Filter Bar -->
    <div ref="filterBar" class="filter-bar border border-border-normal bg-white bg-opacity-90" style="backdrop-filter: blur(6px); position: sticky; top: 16px; z-index: 40000;">
      <FilterTopBar
        ref="filterTopBar"
        no-search
        :keyword-search-opened="openKeywordSearch"
        :text-search="textSearch"
        :active-filters.sync="activeFilters"
        :selected-date-range.sync="selectedDateRange"
        :sort-order.sync="sortOrder"
        @change="fetchAdvertisements"
        @resetKeywordSearch="openKeywordSearch = false"
      />
    </div>

    <div>
      <!-- Loading State -->
      <BaseLoadingLogo
        v-if="loadingAdvertisements"
        class="mt-24"
      />

      <!-- Empty State With Filters -->
      <div v-else-if="!advertisements.length" class="w-full flex flex-col items-center mt-24 fade-in">
        <img src="https://publicassets.foreplay.co/lens-empty.png" style="width: 226px; height: 212px;">
        <BaseText type="heading" size="md" class="text-black mt-9">
          {{ textSearch.length ? 'No results found' : 'Too many filters' }}
        </BaseText>
        <BaseText type="body" size="md" class="text-text-normal mt-2 text-center" style="max-width: 390px;">
          We were unable to find any ads that meet your filter or search criteria.
        </BaseText>
        <button class="secondary-btn flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-md mt-9"
        @click="clearFilters">
          <ClearFiltersIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Clear filters
          </BaseText>
        </button>
      </div>

      <!-- Advertisements List -->
      <AdvertisementList
        v-else
        class="p-4"
        :discovery="true"
        :advertisements="advertisements"
        :loaded="!loadingAdvertisements"
        @updateAds="updateAdsLocal"
        @getMoreAds="handleMoreAdsRequest"
        @reload="fetchAdvertisements(false)"
      />

      <infinite-loading
        v-if="advertisements.length"
        :identifier="infiniteId"
        @infinite="handleInfiniteLoading"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>
    </div>
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import { mapGetters } from 'vuex'

import LensRequests from '@/api/lens/LensRequests'
import AdvertisementList from '../../components/advertisements/AdvertisementList'
import FilterTopBar from '../../components/search/FilterTopBar.vue'
import ClearFiltersIcon from '../../components/globals/Icons/FilterIcons/ClearFiltersIcon.vue'

export default {
  name: 'LensInspirationView',
  components: {
    AdvertisementList,
    FilterTopBar,
    InfiniteLoading,
    ClearFiltersIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      advertisements: [],
      loadingAdvertisements: false,
      activeFilters: {},
      openKeywordSearch: false,
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [],
      lastDocId: null,
      infiniteId: +new Date(),
      textSearch: ''
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved'])
  },
  watch: {
    lensId: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.fetchAdvertisements()
        }
      }
    }
  },
  async mounted () {
    this.selectedLanguages = this.getUser?.defaultLanguages || []
    this.sortOrder = [{ name: 'Newest', value: 'desc' }]
    await this.fetchAdvertisements()
  },
  methods: {
    async handleInfiniteLoading ($state) {
      if (!this.lensId || !this.lastDocId) {
        if ($state?.complete) $state.complete()
        return
      }

      try {
        const filters = {
          ...this.activeFilters,
          textSearch: this.textSearch,
          sort: this.sortOrder.length ? this.sortOrder[0].value : 'desc',
          savedBetweenStart: +new Date(this.selectedDateRange?.start) || null,
          savedBetweenEnd: +new Date(this.selectedDateRange?.end) || null
        }

        const { data: results, nextPage } = await LensRequests.getLensDiscoveryAds(
          this.lensId,
          this.lastDocId,
          filters
        )

        this.lastDocId = nextPage

        if (results && results.length) {
          this.advertisements = this.advertisements.concat(results)
          if ($state?.loaded) $state.loaded()
        } else {
          if ($state?.complete) $state.complete()
        }
      } catch (error) {
        console.error('Error fetching more lens discovery ads:', error)
        if ($state?.error) $state.error()
      }
    },
    handleMoreAdsRequest () {
      this.handleInfiniteLoading({})
    },
    async clearFilters () {
      const filterTopBar = this.$refs.filterTopBar
      filterTopBar.clearAllFilters()
      this.textSearch = ''
      this.sortOrder = [{ name: 'Newest', value: 'desc' }]
      await this.fetchAdvertisements()

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    async fetchAdvertisements (load = true) {
      if (!this.lensId) return

      this.advertisements = []
      this.loadingAdvertisements = load
      this.lastDocId = null
      this.infiniteId += 1

      try {
        const filters = {
          ...this.activeFilters,
          textSearch: this.textSearch,
          sort: this.sortOrder.length ? this.sortOrder[0].value : 'desc',
          savedBetweenStart: +new Date(this.selectedDateRange?.start) || null,
          savedBetweenEnd: +new Date(this.selectedDateRange?.end) || null,
        }

        const { data: results, nextPage } = await LensRequests.getLensDiscoveryAds(
          this.lensId,
          null,
          filters
        )

        this.lastDocId = nextPage
        this.advertisements = results || []
      } catch (e) {
        console.error('Error fetching lens discovery ads:', e)
        this.advertisements = []
      } finally {
        this.loadingAdvertisements = false
      }
    },
    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Lens Inspiration'
  }
}
</script>

<style scoped>
.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}
.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
